import { Button, Spin } from 'antd';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { TriggerAgentRequest } from '@melio/risk-ai-agents-ts-client-config';
import { AgentExecutionType } from '@tools/ui/types/generated/graphql';
import { AgentForm } from './AgentForm';
import { AgentResultCard } from './AgentResultCard';
import { GET_AVAILABLE_RISK_AGENTS } from '../graphql/mutations';
import { BASE_PATH } from '../routes';
import {
  ContentWrapper,
  HeaderSection,
  HeaderTitleRow,
  LoadingCard,
  PageSubtitle,
  PageTitle,
  WarningText,
  PageWrapper,
  StyledAlert,
  StyledBackButton,
  StyledHeaderIcon,
  StyledRerunButton,
} from './RiskAgentRunner.styled';
import { TriggeredStatus } from './TriggeredStatus';
import { POLL_INTERVAL_MS, useRiskAgentRunner } from './useRiskAgentRunner';

const RiskAgentRunner: React.FC = () => {
  const history = useHistory();

  const { data: availableRiskAgentsData, loading: agentsLoading } = useQuery<{ getAvailableRiskAgents: string[] }>(
    GET_AVAILABLE_RISK_AGENTS
  );
  const availableAgents = availableRiskAgentsData?.getAvailableRiskAgents ?? [];

  const [agentName, setAgentName] = useState<string | undefined>(undefined);
  const [entityId, setEntityId] = useState('');
  const [entityType, setEntityType] = useState<TriggerAgentRequest['entity']['type']>('organization');
  const [reason, setReason] = useState('manual-review');
  const [executionType, setExecutionType] = useState<AgentExecutionType>(AgentExecutionType.POST_DECISION);
  const [orchestrationExecutionId, setOrchestrationExecutionId] = useState('');
  const [partnerName, setPartnerName] = useState('melio');

  const { error, isFetching, isPolling, isTriggering, pollAttempts, results, triggered, trigger, reset, waitingMessage } =
    useRiskAgentRunner({ agentName, entityId, entityType, executionType, orchestrationExecutionId, partnerName, reason });

  const hasOutput = triggered.length > 0 || isTriggering;

  return (
    <PageWrapper>
      <ContentWrapper>
        <StyledBackButton
          type="link"
          icon={<ArrowLeftOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />}
          onClick={() => history.push(`${BASE_PATH}/main`)}
        >
          Back to menu
        </StyledBackButton>

        <HeaderSection>
          <HeaderTitleRow>
            <StyledHeaderIcon onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
            <PageTitle>Risk Agent Runner</PageTitle>
          </HeaderTitleRow>
          <PageSubtitle>Quick risk agent executor with results that have pre process layer.</PageSubtitle>
          <WarningText>Please make sure to only run agents that you have been authorized to do so (this can affect production)</WarningText>
        </HeaderSection>

        <AgentForm
          agentName={agentName}
          agentsLoading={agentsLoading}
          availableAgents={availableAgents}
          disabled={hasOutput}
          entityId={entityId}
          entityType={entityType}
          executionType={executionType}
          isTriggering={isTriggering}
          orchestrationExecutionId={orchestrationExecutionId}
          partnerName={partnerName}
          reason={reason}
          onAgentNameChange={setAgentName}
          onEntityIdChange={setEntityId}
          onEntityTypeChange={setEntityType}
          onExecutionTypeChange={setExecutionType}
          onOrchestrationExecutionIdChange={setOrchestrationExecutionId}
          onPartnerNameChange={setPartnerName}
          onReasonChange={setReason}
          onTrigger={trigger}
        />

        {!!hasOutput && (
          <div>
            {isTriggering && (
              <LoadingCard>
                <Spin size="small" />
                <span>Triggering agent…</span>
              </LoadingCard>
            )}

            {triggered.length > 0 && (
              <TriggeredStatus
                isFetching={isFetching}
                isPolling={isPolling}
                pollAttempts={pollAttempts}
                pollIntervalMs={POLL_INTERVAL_MS}
                results={results}
                triggered={triggered}
              />
            )}

            {results.map((res) => (
              <AgentResultCard
                key={res.agentName}
                error={error}
                res={res}
                waitingMessage={waitingMessage}
              />
            ))}

            {(!isPolling && (results.length > 0 || (error !== null && triggered.length > 0))) && (
              <StyledRerunButton size="small" icon={<ReloadOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />} onClick={reset}>
                Run again
              </StyledRerunButton>
            )}
          </div>
        )}

        {!!error && (
          <StyledAlert
            type="error"
            message={error}
            action={
              <Button size="small" icon={<ReloadOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />} onClick={reset}>
                Reset
              </Button>
            }
          />
        )}
      </ContentWrapper>
    </PageWrapper>
  );
};

export { RiskAgentRunner };
export default RiskAgentRunner;
